import { useState } from 'react';
import { ExclamationTriangleIcon, NoSymbolIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useAccountUsage } from '../../hooks/useAccountUsage';
import { useFlag } from '../../hooks/useFeatureFlags';
import {
  SPENDING_PAGE_PATH,
  formatAlertDay,
  formatBilledCents,
  formatSpendingCentsShort,
  formatSpendingMonth,
  spendingReading,
} from '../../utils/spending';

/**
 * SessionStorage key recording which threshold banner the User dismissed, as
 * `<period>:<highest alerted threshold>`. The banner comes back only when the
 * next threshold is crossed (or the month rolls over), which changes the value.
 * Session-scoped like NoPlanBanner's dismissal.
 */
export const SPENDING_BANNER_DISMISSED_KEY = 'spending_banner_dismissed';

function readDismissed(): string | null {
  try {
    return sessionStorage.getItem(SPENDING_BANNER_DISMISSED_KEY);
  } catch {
    return null;
  }
}

/**
 * The two app-wide spending banners for owners and admins (task 1159; report
 * decision 5 — they are the ones who can act). Rendered from AppLayout under
 * the top bar, beside NoPlanBanner. Members see neither; the figures are
 * already on their Dashboard and Billing page.
 *
 *   - Threshold crossed (`alertsSent.length > 0 && !stopped`): amber,
 *     dismissible, back on the next threshold.
 *   - Hard stop (`stopped === true`): red, persistent until calls resume.
 *
 * Each links to Settings › Billing › Spending when the User can open it
 * (`innovate.fe.spending.page`); otherwise the text stands alone.
 */
export default function SpendingBanner() {
  // The spending limit's own lever (task 1217), split off innovate.fe.account.manage.
  const canManage = useFlag('innovate.fe.account.spending');
  const canOpenSpending = useFlag('innovate.fe.spending.page');
  const { data: usage } = useAccountUsage();
  const [dismissed, setDismissed] = useState<string | null>(readDismissed);

  const reading = spendingReading(usage?.spending);
  if (!canManage || !usage || !reading.hasLimit) return null;
  const limitShort = formatSpendingCentsShort(reading.limitCents as number);

  if (reading.stopped) {
    const reachedAlert = usage.spending?.alertsSent.find((a) => a.thresholdPct >= 100);
    return (
      <div
        role="alert"
        data-testid="spending-stopped-banner"
        className="bg-red-50 dark:bg-red-900/20 border-b border-red-200 dark:border-red-800"
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3">
          <NoSymbolIcon className="h-5 w-5 text-red-500 flex-shrink-0" />
          <p className="flex-1 text-sm text-red-900 dark:text-red-200">
            External calls to your AI Services are being refused: the <b>{limitShort} monthly spending limit</b> was
            reached{reachedAlert ? ` on ${formatAlertDay(reachedAlert.sentAt)}` : ''}.{' '}
            {canOpenSpending ? (
              <a
                href={SPENDING_PAGE_PATH}
                data-testid="spending-stopped-banner-link"
                className="font-semibold text-red-800 dark:text-red-300 underline hover:text-red-900 dark:hover:text-red-200"
              >
                Raise the limit or turn off the stop
              </a>
            ) : (
              'Raise the limit or turn off the stop to resume.'
            )}
          </p>
        </div>
      </div>
    );
  }

  if (!reading.crossed || reading.highestAlertPct === null) return null;
  const dismissKey = `${usage.period}:${reading.highestAlertPct}`;
  if (dismissed === dismissKey) return null;

  const handleDismiss = () => {
    try {
      sessionStorage.setItem(SPENDING_BANNER_DISMISSED_KEY, dismissKey);
    } catch {
      // Storage unavailable: the dismissal still holds for this render tree.
    }
    setDismissed(dismissKey);
  };
  const month = formatSpendingMonth(usage.period, false);

  return (
    <div
      role="status"
      data-testid="spending-threshold-banner"
      className="bg-amber-50 dark:bg-amber-900/20 border-b border-amber-200 dark:border-amber-800"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-2.5 flex items-center gap-3">
        <ExclamationTriangleIcon className="h-5 w-5 text-amber-500 flex-shrink-0" />
        <p className="flex-1 text-sm text-amber-900 dark:text-amber-200">
          Your account has used <b>{reading.highestAlertPct}%</b> of its {limitShort} monthly spending limit (
          {formatBilledCents(reading.billedCents)} so far{month ? ` in ${month}` : ''}).{' '}
          {canOpenSpending && (
            <a
              href={SPENDING_PAGE_PATH}
              data-testid="spending-threshold-banner-link"
              className="font-semibold text-amber-800 dark:text-amber-300 underline hover:text-amber-900 dark:hover:text-amber-200"
            >
              Review spending
            </a>
          )}
        </p>
        <button
          type="button"
          onClick={handleDismiss}
          data-testid="spending-threshold-banner-dismiss"
          aria-label="Dismiss"
          className="flex-shrink-0 text-amber-500 hover:text-amber-700 dark:hover:text-amber-300"
        >
          <XMarkIcon className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
